import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  FilePdf, EnvelopeSimple, TrendUp, Buildings,
} from "@phosphor-icons/react";
import client from "../lib/api";

/**
 * RateCardShareCard — Admin metrics widget for the Rate Card share
 * funnel. Proves shipper-facing distribution is real by counting:
 *   - PDF downloads (audit: `rate_card_pdf_downloaded`)
 *   - Emails sent  (audit: `rate_card_email_sent`)
 *
 * Shows totals, download:email split, and top 5 sharing fleets.
 */
export default function RateCardShareCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: d } = await client.get("/admin/rate-card-shares?days=30");
        if (alive) setData(d);
      } catch (e) {
        if (alive) toast.error(e?.response?.data?.detail || "Failed to load rate card shares");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="card-surface p-5" data-testid="rate-card-share-loading">
        <div className="animate-pulse text-neutral-500 text-sm">Loading rate-card share metric…</div>
      </div>
    );
  }
  if (!data) return null;

  const total = data.total_shares || 0;
  const downloads = data.total_downloads || 0;
  const emails = data.total_emails || 0;
  const emailPct = total > 0 ? Math.round((emails / total) * 100) : 0;
  const downloadPct = total > 0 ? 100 - emailPct : 0;

  // Sparkline points from series
  const series = data.series || [];
  const maxVal = Math.max(
    1,
    ...series.map((s) => (s.downloads || 0) + (s.emails || 0)),
  );
  const w = 300;
  const h = 60;
  const points = series.length > 1
    ? series.map((s, i) => {
        const x = (i / (series.length - 1)) * w;
        const v = (s.downloads || 0) + (s.emails || 0);
        const y = h - (v / maxVal) * h;
        return `${x},${y}`;
      }).join(" ")
    : "";

  return (
    <div className="card-surface p-5" data-testid="rate-card-share-card">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 mb-4">
        <TrendUp size={13} className="text-violet-400" />
        Rate Card Shares · Last {data.days}d
      </div>

      <div className="flex items-baseline gap-3 mb-3">
        <div className="text-4xl font-display font-semibold text-white" data-testid="rate-card-share-total">
          {total}
        </div>
        <div className="text-xs text-neutral-500">
          total shares · {data.unique_sharing_fleets || 0} fleet{data.unique_sharing_fleets === 1 ? "" : "s"}
        </div>
      </div>

      {/* Split visualiser: download vs email as one horizontal bar */}
      {total > 0 && (
        <div className="mb-4">
          <div className="flex rounded-full overflow-hidden h-2.5 bg-white/[0.05] border border-white/5">
            {downloads > 0 && (
              <div
                className="bg-emerald-500"
                style={{ width: `${downloadPct}%` }}
                data-testid="rate-card-share-download-bar"
              />
            )}
            {emails > 0 && (
              <div
                className="bg-violet-500"
                style={{ width: `${emailPct}%` }}
                data-testid="rate-card-share-email-bar"
              />
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 mt-2 text-xs">
            <div className="inline-flex items-center gap-1.5 text-neutral-300" data-testid="rate-card-share-download-stat">
              <FilePdf size={11} weight="fill" className="text-emerald-400" />
              <b className="tabular-nums">{downloads}</b>
              <span className="text-neutral-500">downloads ({downloadPct}%)</span>
            </div>
            <div className="inline-flex items-center gap-1.5 text-neutral-300 justify-end" data-testid="rate-card-share-email-stat">
              <EnvelopeSimple size={11} weight="fill" className="text-violet-400" />
              <b className="tabular-nums">{emails}</b>
              <span className="text-neutral-500">emails ({emailPct}%)</span>
            </div>
          </div>
        </div>
      )}

      {/* Sparkline */}
      {points && (
        <svg
          viewBox={`0 0 ${w} ${h}`}
          preserveAspectRatio="none"
          className="w-full h-14 mb-3"
          data-testid="rate-card-share-sparkline"
        >
          <polyline
            points={points}
            fill="none"
            stroke="#a78bfa"
            strokeWidth="2"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      )}

      {/* Top 5 fleets */}
      {(data.top_fleets || []).length > 0 ? (
        <div className="pt-3 border-t border-white/[0.05]">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
            Top sharing fleets
          </div>
          <div className="space-y-1.5" data-testid="rate-card-share-top-fleets">
            {data.top_fleets.map((f) => (
              <div
                key={f.owner_id}
                className="flex items-center justify-between text-xs py-1"
                data-testid={`rate-card-share-fleet-${f.owner_id}`}
              >
                <div className="inline-flex items-center gap-1.5 min-w-0">
                  <Buildings size={11} className="text-neutral-500 shrink-0" />
                  <span className="text-neutral-200 truncate">
                    {f.company_name || f.email || "Unknown"}
                  </span>
                </div>
                <div className="text-neutral-500 font-mono tabular-nums shrink-0 ml-2">
                  <span className="text-emerald-400">{f.downloads}↓</span>
                  <span className="text-neutral-600 mx-1">·</span>
                  <span className="text-violet-300">{f.emails}✉</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : total === 0 && (
        <div className="pt-3 border-t border-white/[0.05] text-xs text-neutral-500 leading-relaxed"
             data-testid="rate-card-share-empty">
          No shares in the last {data.days} days yet. Fleets that download OR email their rate card show up here.
        </div>
      )}
    </div>
  );
}
